import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FounderLetter from "@/components/FounderLetter";
import {
  SealCheckIcon,
  ShieldCheckIcon,
  LightningIcon,
  SparkleIcon,
  CoinsIcon,
  CameraIcon,
  GavelIcon,
  HandshakeIcon,
  ClockCountdownIcon,
  EyeIcon,
  UsersThreeIcon,
  StackIcon,
  TrophyIcon,
  CheckIcon,
  XIcon,
  ArrowRightIcon,
  WarningIcon,
} from "@phosphor-icons/react";

// ---------- Comparison table data ----------
// One fact per row. "Others" deliberately generic (no named competitor).
const COMPARISON = [
  {
    row: "Seller commission",
    bidorah: { good: true, label: "5% retail · 4.5% wholesale" },
    others: { good: false, label: "8% flat" },
  },
  {
    row: "Photo-ID verified sellers",
    bidorah: { good: true, label: "Mandatory · U.S. gov ID" },
    others: { good: false, label: "Not required" },
  },
  {
    row: "Wholesale lane (B2B)",
    bidorah: { good: true, label: "Fully separated · admin-gated" },
    others: { good: false, label: "Doesn't exist" },
  },
  {
    row: "Certificate-grade authentication",
    bidorah: { good: true, label: "PSA · SGC · BGS · StockX badge" },
    others: { good: false, label: "Seller's word only" },
  },
  {
    row: "Smart price appraisal",
    bidorah: { good: true, label: "Photo-based · built-in" },
    others: { good: false, label: "None" },
  },
  {
    row: "Anti-shill detector",
    bidorah: { good: true, label: "5 heuristics · admin-reviewed" },
    others: { good: false, label: "Reactive reports" },
  },
  {
    row: "Auto-snipe protection",
    bidorah: { good: true, label: "30-sec auto-extend (rolling)" },
    others: { good: false, label: "Hard cutoff" },
  },
  {
    row: "Public collector profiles",
    bidorah: { good: true, label: "Badges · wins · share links" },
    others: { good: false, label: "Hidden" },
  },
  {
    row: "Stream wrap cards",
    bidorah: { good: true, label: "1-tap Instagram-ready recap" },
    others: { good: false, label: "Not available" },
  },
];

// ---------- Feature blocks ----------
const PILLARS = [
  {
    icon: ShieldCheckIcon,
    title: "Trust, enforced",
    kicker: "Not promised. Enforced.",
    body: "Every seller is U.S. photo-ID verified. Every wholesale buyer submits a TIN, reseller permit, and business entity — admin-approved before they can bid. High-ticket lots carry verifiable PSA/SGC/BGS/StockX certs. One tap deep-links to the grader's own site to confirm.",
  },
  {
    icon: CoinsIcon,
    title: "Economics that favor sellers",
    kicker: "Half the commission. Instant payouts.",
    body: "5% retail, 4.5% wholesale — still well below what other live-commerce apps charge. Tips pass through 100% — Bidorah takes nothing. Founder Sellers pay 0% commission for their first 14 days, starting the day their application is approved. Instant Stripe payouts land in minutes, not days.",
  },
  {
    icon: SparkleIcon,
    title: "Smart pricing where it actually helps",
    kicker: "Snap a photo. Get a price.",
    body: "Bidoria appraises your listing in under 3 seconds — market value, reserve, Buy-It-Now, confidence, and recent comparable sales. Built for sellers who want to price right the first time, not guess and lose.",
  },
];

const FEATURES = [
  {
    icon: GavelIcon,
    title: "Auto-snipe protection",
    body: "Any bid in the last 30 seconds rolls the clock forward. Snipers can't steal auctions.",
  },
  {
    icon: EyeIcon,
    title: "Anti-shill engine",
    body: "Flags withdraw-rebid tricks, 24-hour-old accounts, IP coordination, and bid-jumping. Admin reviews every flag.",
  },
  {
    icon: TrophyIcon,
    title: "Public collector profiles",
    body: "Every win is a flex. Badges, milestones, and a shareable profile turn buyers into billboards.",
  },
  {
    icon: CameraIcon,
    title: "Live stream wrap cards",
    body: "End a show → get an Instagram-ready recap card showing peak viewers, revenue, and tips. One tap to share.",
  },
  {
    icon: StackIcon,
    title: "Strictly separated lanes",
    body: "Wholesale and retail never mix. Approved B2B buyers see trade pricing. Retail sees retail. No crossover.",
  },
  {
    icon: SealCheckIcon,
    title: "Certificate-verified auctions",
    body: "Upload a PSA/SGC/BGS/StockX cert. Buyers tap-through to the grader's own verification page.",
  },
  {
    icon: HandshakeIcon,
    title: "Founder Seller program",
    body: "First 250 approved sellers: 0% commission for 14 days (clock starts on approval day) + a permanent Founder badge on your shop.",
  },
  {
    icon: LightningIcon,
    title: "Instant payouts",
    body: "Stripe Instant drops your sales into your bank account in minutes — not 1 to 3 business days.",
  },
  {
    icon: UsersThreeIcon,
    title: "Real-time WebRTC video",
    body: "Sub-second live streaming powered by LiveKit. 1080p VP9 with simulcast. 100+ viewers per show, no stutter.",
  },
  {
    icon: ClockCountdownIcon,
    title: "Stream schedule + bookmarks",
    body: "Bookmarkers get an instant push when their followed sellers go live. No more missing the show you cared about.",
  },
  {
    icon: WarningIcon,
    title: "Dispute resolution, posted",
    body: "Published 48-hour SLA on buyer disputes. Escrow-by-default. Money-back on not-as-described items.",
  },
  {
    icon: SparkleIcon,
    title: "Smart price appraisal",
    body: "Snap a photo → get back market value, reserve, BIN, confidence, and 3 comparable recent sales in seconds.",
  },
];

// ---------- Page ----------
export default function WhyBidorah() {
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="why-bidorah-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-zinc-50 to-[#1D78AB]/10">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-[#1D78AB]/20 blur-3xl pointer-events-none"
        />
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 relative">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase bg-zinc-900 text-white rounded-full px-3 py-1.5">
            <SparkleIcon size={11} weight="fill" /> Why Bidorah
          </div>
          <h1 className="mt-5 font-display font-black text-5xl sm:text-7xl tracking-tighter text-zinc-900 leading-[0.9] max-w-4xl">
            Live auctions,
            <br />
            <span className="text-[#1D78AB]">made honest.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-zinc-700 max-w-2xl leading-relaxed">
            Other platforms made live-stream shopping popular. Bidorah made it
            trustworthy, profitable for sellers, and built for buyers who actually
            care whether their Charizard is real.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/apply-to-sell"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-sm font-bold"
              data-testid="why-apply-cta"
            >
              Apply to be a Founder Seller <ArrowRightIcon size={14} weight="bold" />
            </Link>
            <Link
              to="/browse"
              className="inline-flex items-center px-5 py-3 rounded-full border border-zinc-300 hover:border-zinc-400 bg-white text-zinc-800 text-sm font-semibold"
            >
              Browse the marketplace
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-[11px] font-mono tracking-widest uppercase text-zinc-500 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <CheckIcon size={12} weight="bold" className="text-emerald-600" />
              5% commission
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckIcon size={12} weight="bold" className="text-emerald-600" />
              Instant payouts
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckIcon size={12} weight="bold" className="text-emerald-600" />
              Photo-ID verified sellers
            </span>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <p className="text-xs font-mono tracking-widest uppercase text-zinc-500">
          ━━ The three pillars
        </p>
        <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter text-zinc-900">
          What makes Bidorah different.
        </h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="why-pillars">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="rounded-3xl bg-white border border-zinc-200 p-6 hover:border-[#1D78AB] transition-colors"
            >
              <div className="h-12 w-12 rounded-2xl bg-[#1D78AB] grid place-items-center text-white">
                <p.icon size={24} weight="fill" />
              </div>
              <p className="mt-4 text-[10px] font-mono tracking-widest uppercase text-zinc-400">
                {p.kicker}
              </p>
              <h3 className="mt-1 font-display font-black text-xl tracking-tight text-zinc-900">
                {p.title}
              </h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
          <p className="text-xs font-mono tracking-widest uppercase text-zinc-500">
            ━━ Side-by-side
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className="bidorah-lowest-pill inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black text-amber-900 px-3 py-1.5 rounded-full whitespace-nowrap relative overflow-visible"
              data-testid="why-lowest-pill"
            >
              <CoinsIcon size={12} weight="fill" className="relative z-10" />
              <span className="relative z-10">Lowest fees in the industry</span>
              {/* Twinkling sparkle particles — orbit the pill */}
              <span aria-hidden className="bidorah-lowest-sparkle bidorah-lowest-sparkle-1">✦</span>
              <span aria-hidden className="bidorah-lowest-sparkle bidorah-lowest-sparkle-2">✦</span>
              <span aria-hidden className="bidorah-lowest-sparkle bidorah-lowest-sparkle-3">✦</span>
              <span aria-hidden className="bidorah-lowest-sparkle bidorah-lowest-sparkle-4">✦</span>
            </span>
          </div>
          <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl tracking-tighter text-zinc-900">
            Bidorah vs the rest.
          </h2>
          <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
            Every row below is verifiable. Every seller signed up on Bidorah
            today gets all of it — no enterprise tier, no gated features.
          </p>

          <div
            className="mt-8 rounded-2xl sm:rounded-3xl border border-zinc-200 overflow-hidden"
            data-testid="why-comparison"
          >
            <div className="grid grid-cols-[1.2fr_1fr_1fr] sm:grid-cols-[1fr_1.1fr_1fr] text-[10px] sm:text-xs font-mono tracking-widest uppercase bg-zinc-900 text-white">
              <div className="px-2.5 sm:px-4 py-2.5 sm:py-3">Feature</div>
              <div className="px-2.5 sm:px-4 py-2.5 sm:py-3 border-l border-zinc-700 bg-[#1D78AB]">
                Bidorah
              </div>
              <div className="px-2.5 sm:px-4 py-2.5 sm:py-3 border-l border-zinc-700 text-zinc-300">
                Others
              </div>
            </div>
            {COMPARISON.map((r, i) => (
              <div
                key={r.row}
                className={`grid grid-cols-[1.2fr_1fr_1fr] sm:grid-cols-[1fr_1.1fr_1fr] text-xs sm:text-sm ${
                  i % 2 === 0 ? "bg-white" : "bg-zinc-50"
                }`}
              >
                <div className="px-2.5 sm:px-4 py-3 sm:py-4 font-semibold text-zinc-900 leading-snug">
                  {r.row}
                </div>
                <div className="px-2.5 sm:px-4 py-3 sm:py-4 border-l border-zinc-200 flex items-start gap-1.5 sm:gap-2">
                  <CheckIcon size={12} weight="bold" className="text-emerald-600 mt-[3px] shrink-0 sm:hidden" />
                  <CheckIcon size={14} weight="bold" className="text-emerald-600 mt-0.5 shrink-0 hidden sm:block" />
                  <span className="text-zinc-900 font-semibold leading-snug">{r.bidorah.label}</span>
                </div>
                <div className="px-2.5 sm:px-4 py-3 sm:py-4 border-l border-zinc-200 flex items-start gap-1.5 sm:gap-2">
                  <XIcon size={12} weight="bold" className="text-red-500 mt-[3px] shrink-0 sm:hidden" />
                  <XIcon size={14} weight="bold" className="text-red-500 mt-0.5 shrink-0 hidden sm:block" />
                  <span className="text-zinc-600 leading-snug">{r.others.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full feature grid */}
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <p className="text-xs font-mono tracking-widest uppercase text-zinc-500">
          ━━ The whole toolbox
        </p>
        <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl tracking-tighter text-zinc-900">
          Everything you get on day one.
        </h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="why-features">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl bg-white border border-zinc-200 p-5 hover:border-[#1D78AB]/60 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-xl bg-[#1D78AB]/10 grid place-items-center text-[#1D78AB] shrink-0">
                  <f.icon size={18} weight="fill" />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-black text-sm tracking-tight text-zinc-900">
                    {f.title}
                  </p>
                  <p className="mt-1 text-xs text-zinc-600 leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Seller quote */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
          <blockquote className="max-w-3xl">
            <p className="font-display font-black text-2xl sm:text-4xl tracking-tighter leading-tight">
              "Half the commission, real photo-ID on every seller, and my buyers
              can actually see that my Charizard is a real PSA 10. Why would I
              stream anywhere else?"
            </p>
            <footer className="mt-6 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#1D78AB] grid place-items-center font-display font-black text-white">
                C
              </div>
              <div>
                <p className="font-semibold text-sm">@cardhouse_steve</p>
                <p className="text-[11px] font-mono tracking-widest uppercase text-zinc-400">
                  Founder Seller · Trading Cards · Trust Tier A
                </p>
              </div>
            </footer>
          </blockquote>
        </div>
      </section>

      {/* CTA footer */}
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-24 text-center">
        <p className="text-xs font-mono tracking-widest uppercase text-zinc-500">
          ━━ Join the founders
        </p>
        <h2 className="mt-3 font-display font-black text-4xl sm:text-6xl tracking-tighter text-zinc-900 leading-[0.95]">
          Stream your first
          <br />
          show this week.
        </h2>
        <p className="mt-5 text-sm sm:text-base text-zinc-600 max-w-xl mx-auto">
          The first 250 approved Founder Sellers pay 0% commission for 14 days
          and get a permanent Founder badge on their shop.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/apply-to-sell"
            className="inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-sm font-bold shadow-lg shadow-[#1D78AB]/20"
            data-testid="why-final-cta"
          >
            Apply to sell on Bidorah <ArrowRightIcon size={14} weight="bold" />
          </Link>
          <Link
            to="/browse"
            className="inline-flex items-center px-6 py-3.5 rounded-full border border-zinc-300 hover:border-zinc-400 bg-white text-zinc-800 text-sm font-semibold"
          >
            Just browsing
          </Link>
        </div>
      </section>

      {/* FROM THE FOUNDER — embedded teaser into the canonical /letter */}
      <FounderLetter variant="excerpt" />

      <Footer />
    </div>
  );
}
